import * as THREE from 'three';
import type { V2 } from '../../core/math';
import type { ZoneRuntime } from '../../game/ZoneRuntime';

/**
 * Presentation-only effects for scripted instance objects (never created in
 * headless runs). No lights are added: a new light would force every material
 * in the scene to recompile.
 */

export interface PortalFx {
  update(dt: number): void;
  dispose(): void;
}

function additive(color: number, opacity: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false });
}

/** A swirling rift gate: ground ring, spinning inner rings and a translucent light column. */
export function portalFx(z: ZoneRuntime, at: V2, color: number, radius = 2.6): PortalFx {
  const g = new THREE.Group();
  const rim = new THREE.Mesh(new THREE.RingGeometry(radius * 0.86, radius, 64), additive(color, 0.9));
  rim.rotation.x = -Math.PI / 2;
  rim.position.y = 0.06;
  const inner = new THREE.Mesh(new THREE.RingGeometry(radius * 0.35, radius * 0.72, 5, 1), additive(color, 0.45));
  inner.rotation.x = -Math.PI / 2;
  inner.position.y = 0.08;
  const pool = new THREE.Mesh(new THREE.CircleGeometry(radius * 0.84, 48), additive(color, 0.22));
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.05;
  const column = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.7, radius * 0.9, 5.5, 40, 1, true), additive(color, 0.16));
  column.position.y = 2.75;
  const halo = new THREE.Mesh(new THREE.TorusGeometry(radius * 0.62, 0.07, 8, 48), new THREE.MeshStandardMaterial({ color: 0x101010, emissive: color, emissiveIntensity: 3 }));
  halo.rotation.x = Math.PI / 2;
  halo.position.y = 1.6;
  const meshes = [rim, inner, pool, column, halo];
  for (const m of meshes) {
    m.renderOrder = 3;
    m.castShadow = false;
    m.receiveShadow = false;
  }
  g.add(rim, inner, pool, column, halo);
  g.position.set(at.x, z.heightAt(at.x, at.z), at.z);
  z.scene.add(g);
  let t = 0;
  return {
    update(dt: number): void {
      t += dt;
      inner.rotation.z += dt * 0.9;
      column.rotation.y -= dt * 0.6;
      halo.position.y = 1.6 + Math.sin(t * 1.7) * 0.35;
      column.material.opacity = 0.13 + Math.sin(t * 2.3) * 0.04;
    },
    dispose(): void {
      z.scene.remove(g);
      for (const m of meshes) {
        m.geometry.dispose();
        m.material.dispose();
      }
    },
  };
}
